import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  usePlateEditorRef,
  useEventPlateId,
  setMarks,
  getMarks,
  focusEditor,
} from '@udecode/plate';
import clsx from 'clsx';

import ToolbarDropdown from '../../dropdown/ToolbarDropdown';

import './font-size-control.scss';

const DEFAULT_FONT_SIZE = 13;
const DEFAULT_SIZE_UNIT = 'px';
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 96;
const INCREMENT_SIZE = 1;

const handleSizeChange = (editor, value, unit) => {
  if (unit === 'px' && (value >= MIN_FONT_SIZE || value <= MAX_FONT_SIZE))
    setMarks(editor, { fontSize: `${parseInt(value, 10)}${unit}` });
};

const incrementSize = (editor, currentValue, unit) => {
  const newSize = parseInt(currentValue, 10) + INCREMENT_SIZE;
  handleSizeChange(editor, newSize, unit);
};

const decrementSize = (editor, currentValue, unit) => {
  const newSize = parseInt(currentValue, 10) - INCREMENT_SIZE;
  handleSizeChange(editor, newSize, unit);
};

export const FontSizeControl = () => {
  const editor = usePlateEditorRef(useEventPlateId());
  const marks = getMarks(editor);
  const { fontSize } = marks || {};
  const { size, unit } = useMemo(() => {
    if (fontSize) {
      const sizeMatch = fontSize.match(/\d+/);
      const unitMatch = fontSize.match(/[a-z]+/i);
      const parsedSize = sizeMatch && sizeMatch.length > 0 ? sizeMatch[0] : DEFAULT_FONT_SIZE;
      const parsedUnit = unitMatch && unitMatch.length > 0 ? unitMatch[0] : DEFAULT_SIZE_UNIT;
      return { size: parsedSize, unit: parsedUnit };
    }
    return { size: DEFAULT_FONT_SIZE, unit: DEFAULT_SIZE_UNIT };
  }, [editor.selection, fontSize]);

  const inputRef = useRef();

  // Controlled input value needs to be decoupled with memo value.
  const [inputValue, setInputValue] = useState(size);

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const setFontSize = (e, _size) => {
    e.preventDefault();
    e.stopPropagation();
    handleSizeChange(editor, _size, unit);
  };

  // Update controlled input value when size changes.
  useEffect(() => {
    setInputValue(size);
  }, [size]);

  return (
    <div className="font-size-control">
      <button
        className="button--unstyled font-size-control__button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          decrementSize(editor, size, unit);
          focusEditor(editor);
        }}
      >
        -
      </button>
      <input
        ref={inputRef}
        className="font-size-control__input"
        value={inputValue}
        type="number"
        min={6}
        max={100}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={(e) => {
          const value = parseInt(e.target.value, 10);
          if (value) {
            handleSizeChange(editor, e.target.value, unit);
          } else {
            setInputValue(size);
          }
          setDropdownOpen(false);
        }}
        onFocus={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setDropdownOpen(true);
        }}
        onClick={(e) => {
          e.preventDefault();
          setDropdownOpen(true);
        }}
        onMouseDown={(e) => {
          // To prevent selected text from unhighlighting when clicking input.
          e.stopPropagation();
          e.preventDefault();
        }}
      />
      <button
        className="button--unstyled font-size-control__button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          incrementSize(editor, size, unit);
          focusEditor(editor);
        }}
      >
        +
      </button>
      {isDropdownOpen && (
        <SizePickerDropdown
          isOpen={isDropdownOpen}
          setFontSize={setFontSize}
          currentSize={size}
          inputRef={inputRef}
          dismiss={() => setDropdownOpen(false)}
        />
      )}
    </div>
  );
};

const SizePickerDropdown = ({ setFontSize, currentSize, inputRef, dismiss }) => {
  // Need to separately handle dismissing from outside click because the dropdown
  // trigger is outside of the dropdown component.
  const handleDismiss = (e) => {
    // Always close if click target is not the input.
    if (!(inputRef.current).contains(e.target)) dismiss();
  };

  return (
    <ToolbarDropdown
      isOpen
      className="plate-toolbar__dropdown toolbar-dropdown toolbar-dropdown--font-size"
      anchorElement={inputRef?.current}
      root={null}
      dismiss={handleDismiss}
      placement="bottom"
    >
      {FONT_DROPDOWN_SIZES.map(size => {
        return (
          <button
            key={`font-size--${size}`}
            className={clsx(
              'button--unstyled',
              'button--editor-menu',
              currentSize === size && 'active',
            )}
            onClick={(e) => {
              setFontSize(e, size);
              dismiss();
            }}
          >
            {size}
          </button>
        );
      })}
    </ToolbarDropdown>
  );
};

const FONT_DROPDOWN_SIZES = [
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  18,
  24,
  30,
  36,
  48,
  60,
  72,
  96,
];
